// src/components/aiondemand/JobResultLayers.jsx

import { useEffect, useRef } from 'react';
import { useAIOnDemandStore } from '../../stores/useAIOnDemandStore';
import { fetchWithAuth } from '../../api/fetchWithAuth';
import { getActionById } from '../../actions/registry.js';

const SOURCE_PREFIX = 'aiod-job-result-';
const LAYER_PREFIX = 'aiod-job-result-';

export default function JobResultLayers({ mapRef, mapLoaded }) {
  const jobLayerVisible = useAIOnDemandStore((s) => s.jobLayerVisible);
  const jobs = useAIOnDemandStore((s) => s.jobs);
  const archivedJobs = useAIOnDemandStore((s) => s.archivedJobs);

  const mountedRef = useRef(new Set());

  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !mapLoaded) return;

    const desired = new Set(
      Object.entries(jobLayerVisible)
        .filter(([_, on]) => on)
        .map(([jobId]) => jobId)
    );

    for (const jobId of desired) {
      if (mountedRef.current.has(jobId)) continue;
      const job = jobs.find((j) => j.id === jobId)
        ?? archivedJobs.find((j) => j.id === jobId);
      if (!job || job.status !== 'completed') continue;
      addJobLayer(map, jobId, job);
      mountedRef.current.add(jobId);
    }

    for (const jobId of [...mountedRef.current]) {
      if (desired.has(jobId)) continue;
      removeJobLayer(map, jobId);
      mountedRef.current.delete(jobId);
    }
  }, [mapRef, mapLoaded, jobLayerVisible, jobs]);

  useEffect(() => {
    return () => {
      const map = mapRef?.current;
      if (!map) return;
      for (const jobId of mountedRef.current) {
        removeJobLayer(map, jobId);
      }
      mountedRef.current.clear();
    };
  }, [mapRef]);

  return null;
}

async function addJobLayer(map, jobId, job) {
  const action = getActionById(job.task_key);
  const color = action?.displayColor ?? '#a78bfa';

  const sourceId = `${SOURCE_PREFIX}${jobId}`;

  // Initial empty source
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  // Fetch the GeoJSON
  let geojson = null;
  try {
    const res = await fetchWithAuth(`/api/v1/jobs/${jobId}/result?key=geojson`);
    if (!res.ok) {
      console.warn(`[JobResultLayers] result fetch failed for ${jobId}: ${res.status}`);
      return;
    }
    geojson = await res.json();
  } catch (err) {
    console.error(`[JobResultLayers] result fetch error for ${jobId}`, err);
    return;
  }

  // Detect what geometry types are in the result
  const hasPolygons = geojson.features?.some(
    (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
  );
  const hasPoints = geojson.features?.some(
    (f) => f.geometry?.type === 'Point' || f.geometry?.type === 'MultiPoint'
  );

  // Add layers per geometry type present
  if (hasPolygons) {
    const fillId = `${LAYER_PREFIX}${jobId}-fill`;
    const lineId = `${LAYER_PREFIX}${jobId}-line`;

    if (!map.getLayer(fillId)) {
      map.addLayer({
        id: fillId,
        type: 'fill',
        source: sourceId,
        filter: ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]],
        paint: {
          'fill-color': color,
          'fill-opacity': 0.18,
        },
      });
    }
    if (!map.getLayer(lineId)) {
      map.addLayer({
        id: lineId,
        type: 'line',
        source: sourceId,
        filter: ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]],
        paint: {
          'line-color': color,
          'line-width': 1.2,
          'line-opacity': 0.85,
        },
      });
    }
  }

  if (hasPoints) {
    const pointId = `${LAYER_PREFIX}${jobId}-point`;
    if (!map.getLayer(pointId)) {
      map.addLayer({
        id: pointId,
        type: 'circle',
        source: sourceId,
        filter: ['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]],
        paint: {
          'circle-color': color,
          'circle-radius': 5,
          'circle-stroke-color': '#000',
          'circle-stroke-width': 1,
          'circle-opacity': 0.85,
        },
      });
    }
  }

  // Set the data
  const src = map.getSource(sourceId);
  if (src) src.setData(geojson);
}

function removeJobLayer(map, jobId) {
  const sourceId = `${SOURCE_PREFIX}${jobId}`;
  const fillId = `${LAYER_PREFIX}${jobId}-fill`;
  const lineId = `${LAYER_PREFIX}${jobId}-line`;
  const pointId = `${LAYER_PREFIX}${jobId}-point`;

  if (map.getLayer(fillId)) map.removeLayer(fillId);
  if (map.getLayer(lineId)) map.removeLayer(lineId);
  if (map.getLayer(pointId)) map.removeLayer(pointId);
  if (map.getSource(sourceId)) map.removeSource(sourceId);
}